import { convertBodyToHtml } from "@/lib/utils/htmlSanitizer";
import {
  decodeHtmlEntities,
  protectInvalidNumericEntities,
} from "./htmlEntities";

export const STUDYGROUP_NAME_TOKEN = "{study_group_name}";
const placeholderSelector = "[data-study-group-name]";
const placeholderRegex = /<span[^>]*data-study-group-name[^>]*>.*?<\/span>/gi;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Preserve user-entered whitespace/newlines for invitation messages.
const inviteHtmlToPlainText = (value?: string | null) => {
  if (!value) return "";
  const hadBlockWrapper = /<(?:div|p)\b/i.test(value);
  const plain = value
    .replace(/\r\n?/g, "\n")
    // Empty block element corresponds to one empty line.
    .replace(/<div[^>]*>\s*(?:<br\s*\/?>)?\s*<\/div>/gi, "\n")
    .replace(/<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, "\n")
    // Block closing boundary corresponds to one line break.
    .replace(/<\/(?:div|p)>/gi, "\n")
    // Remove opening wrappers without adding line breaks.
    .replace(/<(?:div|p)[^>]*>/gi, "")
    // Manual <br> line breaks.
    .replace(/<br\s*\/?>/gi, "\n")
    // Strip all remaining HTML tags.
    .replace(/<[^>]+>/g, "");

  // Converted block wrappers add one synthetic trailing newline.
  if (hadBlockWrapper && plain.endsWith("\n")) {
    return plain.slice(0, -1);
  }

  return plain;
};

export const clampInvitationMessage = (value: string, maxLength: number) =>
  value.length > maxLength ? value.slice(0, maxLength) : value;

export const buildStudyGroupNameBadgeHtml = (label: string) => {
  const displayLabel = escapeHtml(label || "[Study group name]");
  const linkIconHtml =
    '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>';
  return `<span data-study-group-name="true" contenteditable="false" draggable="false" style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:9999px;background:#e0f2fe;color:#0ea5e9;font-weight:600;font-size:12px;user-select:none;pointer-events:none;">${linkIconHtml}${displayLabel}</span>`;
};

export const replaceStudyGroupNameToken = (value: string, badgeHtml: string) =>
  (value ?? "").replaceAll(STUDYGROUP_NAME_TOKEN, badgeHtml);

export const toDisplayInviteMessage = (value: string, badgeHtml: string) => {
  const html = convertBodyToHtml(value ?? "");
  return replaceStudyGroupNameToken(html, badgeHtml);
};

export const insertStudyGroupToken = (current: string) => {
  const needsSeparator = current.length > 0 && !current.trim().endsWith("\n");
  const separator = needsSeparator ? "\n" : "";
  return `${current}${separator}${STUDYGROUP_NAME_TOKEN}`;
};

export const toStoredInviteMessage = (value: string) => {
  if (!value) return "";
  if (typeof DOMParser === "undefined") {
    return decodeHtmlEntities(
      inviteHtmlToPlainText(
        value.replace(placeholderRegex, STUDYGROUP_NAME_TOKEN),
      ),
    );
  }
  const protectedValue = protectInvalidNumericEntities(value);
  const parser = new DOMParser();
  const doc = parser.parseFromString(protectedValue, "text/html");
  const placeholders = doc.body.querySelectorAll(placeholderSelector);
  placeholders.forEach((node) => {
    const tokenText = doc.createTextNode(STUDYGROUP_NAME_TOKEN);
    node.parentNode?.replaceChild(tokenText, node);
  });
  return decodeHtmlEntities(inviteHtmlToPlainText(doc.body.innerHTML || ""));
};

export const normalizeInviteMessageFromHtml = (
  value: string,
  maxLength: number,
) => clampInvitationMessage(toStoredInviteMessage(value), maxLength);

export const buildInviteMessageHtml = (message: string, badgeLabel: string) =>
  toDisplayInviteMessage(message, buildStudyGroupNameBadgeHtml(badgeLabel));
